import { useCallback, useRef, useState } from "react";
import { toast } from "react-toastify";
import { isAcceptedScanFile } from "./constants";

/**
 * Drag-and-drop file selection for scan uploads. Lifted from AiAnswerSheetUpload so the
 * worksheet, past paper and mock exam flows all behave identically.
 */
export default function useScanDropZone() {
  const [file, setFile] = useState(null);
  const [dropZoneActive, setDropZoneActive] = useState(false);
  const dragDepthRef = useRef(0);

  const applyFile = useCallback((f) => {
    if (!f) return;
    if (!isAcceptedScanFile(f)) {
      toast.error("Please upload a PDF, PNG, or JPG file.");
      return;
    }
    setFile(f);
  }, []);

  const handleFileChange = useCallback(
    (event) => {
      const selected = event.target.files?.[0];
      if (selected) applyFile(selected);
      event.target.value = "";
    },
    [applyFile]
  );

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current += 1;
    setDropZoneActive(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setDropZoneActive(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      e.dataTransfer.dropEffect = "copy";
    } catch (_) {}
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragDepthRef.current = 0;
      setDropZoneActive(false);
      const dropped = e.dataTransfer.files?.[0];
      if (dropped) applyFile(dropped);
    },
    [applyFile]
  );

  const reset = useCallback(() => {
    setFile(null);
    dragDepthRef.current = 0;
    setDropZoneActive(false);
  }, []);

  return {
    file,
    setFile,
    dropZoneActive,
    applyFile,
    reset,
    dropZoneProps: {
      onDragEnter: handleDragEnter,
      onDragLeave: handleDragLeave,
      onDragOver: handleDragOver,
      onDrop: handleDrop,
    },
    handleFileChange,
  };
}
